// src/components/network/ForfeoReservationPassPanel.tsx
import { CalendarCheck, QrCode, ShieldCheck, Smartphone, WalletCards } from 'lucide-react';
import ForfeoAppCTA from './ForfeoAppCTA';
import ForfeoNetworkBadges from './ForfeoNetworkBadges';

type ForfeoReservationPassPanelProps = {
  id?: string | number | null;
  kind?: 'reservation' | 'purchase' | 'voucher' | 'experience';
  title?: string;
  subtitle?: string;
  compact?: boolean;
  className?: string;
};

const copyByKind = {
  reservation: {
    eyebrow: 'Pass expérience',
    title: 'Réservation prête pour le wallet Forfeo',
    subtitle:
      'Cette réservation est pensée pour être retrouvée dans l’application mobile Forfeo avec pass, QR sécurisé et statut de validation.',
    cta: 'Ouvrir la réservation dans l’app',
    target: 'reservation' as const,
  },
  purchase: {
    eyebrow: 'Wallet Forfeo',
    title: 'Achat compatible avec le wallet mobile',
    subtitle:
      'Les achats Forfeo seront progressivement centralisés dans l’application : cartes, pass, QR codes et historique.',
    cta: 'Ouvrir mon wallet dans l’app',
    target: 'wallet' as const,
  },
  voucher: {
    eyebrow: 'QR sécurisé',
    title: 'Billet prêt pour validation partenaire',
    subtitle:
      'Le code associé à ce billet pourra être validé par le partenaire dans l’écosystème Forfeo.',
    cta: 'Ouvrir le billet dans l’app',
    target: 'wallet' as const,
  },
  experience: {
    eyebrow: 'Expérience locale',
    title: 'Expérience prête pour l’app Forfeo',
    subtitle:
      'Réservez, conservez et présentez votre pass directement depuis l’application mobile Forfeo.',
    cta: 'Réserver dans l’app',
    target: 'experience' as const,
  },
};

export function ForfeoReservationPassPanel({
  id,
  kind = 'reservation',
  title,
  subtitle,
  compact = false,
  className = '',
}: ForfeoReservationPassPanelProps) {
  const copy = copyByKind[kind];

  if (compact) {
    return (
      <div className={`rounded-2xl border border-blue-100 bg-blue-50/70 p-4 ${className}`.trim()}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="mb-2 inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-blue-700">
              <WalletCards size={13} /> {copy.eyebrow}
            </div>
            <div className="text-sm font-black text-slate-950">{title || copy.title}</div>
            <p className="mt-1 text-xs font-semibold leading-5 text-slate-600">{subtitle || copy.subtitle}</p>
          </div>
          <ForfeoAppCTA target={copy.target} id={id || undefined} label="Ouvrir dans l’app" className="w-full shrink-0 px-4 py-2.5 text-[10px] sm:w-auto" />
        </div>
      </div>
    );
  }

  return (
    <section className={`overflow-hidden rounded-[2rem] border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-violet-50 p-5 shadow-sm ${className}`.trim()}>
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-blue-700">
            <Smartphone size={13} /> {copy.eyebrow}
          </div>
          <h3 className="text-2xl font-black tracking-tight text-slate-950">{title || copy.title}</h3>
          <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-slate-600">{subtitle || copy.subtitle}</p>
          <div className="mt-4">
            <ForfeoNetworkBadges variant="full" />
          </div>
        </div>

        <div className="w-full rounded-3xl border border-white/70 bg-white/80 p-4 shadow-sm lg:max-w-xs">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-2xl bg-slate-50 p-3">
              <WalletCards className="mx-auto mb-2 text-blue-600" size={20} />
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Wallet</div>
            </div>
            <div className="rounded-2xl bg-slate-50 p-3">
              <QrCode className="mx-auto mb-2 text-violet-600" size={20} />
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">QR</div>
            </div>
            <div className="rounded-2xl bg-slate-50 p-3">
              <ShieldCheck className="mx-auto mb-2 text-emerald-600" size={20} />
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Suivi</div>
            </div>
          </div>
          <ForfeoAppCTA target={copy.target} id={id || undefined} label={copy.cta} className="mt-4 w-full" />
          <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-bold text-slate-500">
            <CalendarCheck size={13} /> Aucun paiement ni QR réel modifié par ce lot.
          </div>
        </div>
      </div>
    </section>
  );
}

export default ForfeoReservationPassPanel;
